import Lang from '/@/modules/core/app/services/Lang';
import { FinancialAccountSubTypeOptionsMeta } from '/@/modules/financialaccount/models/meta/FinancialAccountSubTypeOptionsMeta';
import NumeralService from '/@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import EstateTypeLang from '/@/modules/estate/services/EstateTypeLang';
import { AssetCoOwner } from '/@/modules/contact/models/AssetCoOwner';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { getValueAsFloat } from '/@/modules/core/app/helpers/NumberHelper';
import { LiabilityCoOwner } from '@/modules/contact/models/LiabilityCoOwner';
import { AssetType } from '@/modules/asset/enums/AssetType';
import { LiabilityType } from '@/modules/liability/enums/LiabilityType';

export const AccountingOwnershipFormMixin = (superClass) =>
  class extends superClass {
    /**
     * @returns {string}
     */
    get namedBeneficiaries() {
      if (!this.hasNamedBeneficiaries) {
        return Lang.t('accounting.estate');
      }

      return this.estateAccounts
        .map((beneficiary) => beneficiary.userDisplayNameAndPartnerRelationship)
        .join(' - ');
    }

    /**
     * @returns {boolean}
     */
    get hasPartnerAsBeneficiary() {
      return this.estateAccounts?.some((beneficiary) => beneficiary.isPartner);
    }

    /**
     * @returns {boolean}
     */
    get singleBeneficiaryIsPartner() {
      return this.hasPartnerAsBeneficiary && this.estateAccounts?.length === 1;
    }

    /**
     * @returns {boolean}
     */
    get isBankAccountWithRegisteredInvestment() {
      return this.isBankAccount && this.financialAccount?.isRegisteredInvestmentSubType;
    }

    /**
     * @returns {boolean}
     */
    get hasNamedBeneficiaries() {
      return !!this.estateAccounts?.length && this.financialAccount?.isRegisteredInvestmentSubType;
    }

    /**
     * @returns {float|number}
     *  Computed value to the estate for asset before saving
     */
    get computedValueToTheEstateForAsset() {
      if (this.isBankAccountWithRegisteredInvestment) {
        return this.valueToTheEstateForRegisteredInvestment;
      }

      if (this.hasShareOfOwnershipType) {
        const estateOwnershipShare = this.computeEstateShareForOwnershipAssetOrLiability;
        return this.computeValueForEstateFromEstateShare(this.atDeathValue, estateOwnershipShare);
      }

      if (this.isPensionPlan || this.isLifeInsurance) {
        return this.hasEstateAccount ? 0 : this.atDeathValue;
      }

      if (this.hasNamedBeneficiaries) {
        return this.valueToTheEstateForRegisteredInvestment;
      }

      if (this.isIncome) {
        if (!this.hasAsset || !this.atDeathValue) {
          return 0;
        }
        if (this.hasAssetWithOwnership) {
          return getValueAsFloat(this.atDeathValue * this.asset.meta.sharePercentage * 0.01);
        }
        return this.atDeathValue;
      }

      if (this.isPrivateStock) {
        return this.atDeathValue;
      }

      return 0;
    }

    /**
     * @returns {number|float}
     * Computed value to the estate for liability before saving
     */
    get computedValueToTheEstateForLiability() {
      const estateOwnershipShare = this.computeEstateShareForOwnershipAssetOrLiability;
      return this.computeValueForEstateFromEstateShare(
        this.currentBalanceAmount,
        estateOwnershipShare
      );
    }

    set computedValueToTheEstateForLiability(newValue) {
      this.estateValue = newValue;
    }

    set computedValueToTheEstateForAsset(newValue) {
      this.estateValue = newValue;
    }

    /**
     * @returns {number}
     */
    get estateAccountsSumOfShares() {
      if (!this.estateAccounts || this.estateAccounts.length <= 0) {
        return 0;
      }

      return (
        this.estateAccounts.reduce(
          (accum, beneficiary) => accum + beneficiary.pivot?.shareOfOwnership ?? 0,
          0
        ) ?? 0
      );
    }

    /**
     * @returns {string}
     */
    get ownershipSubLabel() {
      if (this.financialAccount?.isRegisteredInvestmentSubType) {
        return this.valueToEstateSubLabelForRegisteredInvestment;
      }
      return this.valueToEstateSubLabelForOwnershipAsset;
    }

    /**
     * @return {string}
     */
    get valueToEstateSubLabelForOwnershipAsset() {
      if (this.meta.isSoleOwnership || this.meta.isJointOwnership) {
        return `${EstateTypeLang.estateValueSubLabel} ${NumeralService.formatValue(
          this.computeEstateShareForOwnershipAssetOrLiability,
          ColumnFormat.FORMAT_PERCENT,
          2
        )}`;
      }
      return Lang.t('accounting.assets-in-automatically');
    }

    /**
     *
     * @return {number|float}
     *
     */
    get computeEstateShareForOwnershipAssetOrLiability() {
      if (this.meta.isSoleOwnership) {
        return 100;
      }
      if (this.meta.isJointSurvivorshipOwnership) {
        return 0;
      }
      if (this.meta.isJointOwnership) {
        const coOwnersShare = this.coOwners.reduce(
          (accum, coOwner) => accum + Number.parseFloat(coOwner.pivot?.shareOfOwnership),
          0
        );
        const estateShare = 100 - coOwnersShare;
        if (estateShare <= 100 || estateShare >= 0) {
          return Math.round(estateShare * 100) / 100;
        }
      }
      return 100;
    }

    /**
     * @return {string}
     */
    get valueToEstateSubLabelForRegisteredInvestment() {
      if (
        this.financialAccount.isAnnuitantTypeMeta ||
        this.financialAccount.isSuccessorHolderTypeMeta
      ) {
        return this.valueToEstateSubLabelForAnnuitantOrSuccessorHolder;
      }
      if (this.hasNamedBeneficiaries) {
        return this.valueToEstateSubLabelForNamedBeneficiaries;
      }
      return '';
    }

    /**
     * @returns {boolean}
     */
    get isAnnuitantOrSuccessorHolderTypeMetaWithoutSpouse() {
      return (
        (this.financialAccount.isSuccessorHolderTypeMeta ||
          this.financialAccount.isAnnuitantTypeMeta) &&
        !this.currentEstate.contact.isMarried
      );
    }

    /**
     * @returns {boolean}
     */
    get investmentBankAccountGoesToEstate() {
      return this.isAnnuitantOrSuccessorHolderTypeMetaWithoutSpouse ? true : !this.hasEstateAccount;
    }

    get canAssignCoOwners() {
      if (AssetType.canAssignCoOwners(this.type)) {
        if (!this.isBankAccount) {
          return true;
        }

        if (!this.financialAccount.isRegisteredInvestmentSubType) {
          return true;
        }

        return false;
      }

      return LiabilityType.canAssignCoOwners(this.type);
    }

    /**
     * @return {string}
     */
    get valueToEstateSubLabelForAnnuitantOrSuccessorHolder() {
      return Lang.t('accounting.the-type-estate', {
        type: FinancialAccountSubTypeOptionsMeta.factory(this.financialAccount.subType).getByKey(
          this.financialAccount.meta?.type
        ).lowercaseValue
      });
    }

    /**
     * @return {string}
     */
    get valueToEstateSubLabelForNamedBeneficiaries() {
      let subLabel = '';
      const estateShare = this.computeEstateShareForRegisteredInvestment;
      const formattedEstateShare = NumeralService.formatValue(
        estateShare,
        ColumnFormat.FORMAT_PERCENT,
        2
      );

      const formattedBeneficiaryShare = NumeralService.formatValue(
        100 - estateShare,
        ColumnFormat.FORMAT_PERCENT,
        2
      );

      subLabel += Lang.t('accounting.the-estateshare-beneficiaryshare', {
        estateShare: formattedEstateShare,
        beneficiaryShare: this.singleBeneficiaryIsPartner
          ? Lang.t('accounting.as-the-deceased')
          : Lang.t('accounting.beneficiaryshare-as-named', {
              beneficiaryShare: formattedBeneficiaryShare
            })
      });

      if (this.hasPartnerAsBeneficiary && !this.singleBeneficiaryIsPartner) {
        subLabel += Lang.t('accounting.part-not-of');
      }
      return subLabel;
    }

    /**
     * @return {float|number}
     */
    get valueToTheEstateForRegisteredInvestment() {
      if (
        this.financialAccount.isAnnuitantTypeMeta ||
        this.financialAccount.isSuccessorHolderTypeMeta ||
        this.financialAccount.isSegregatedFunds
      ) {
        return 0;
      }
      if (this.financialAccount.isNoneTypeMeta || !this.estateAccounts?.length) {
        return this.atDeathValue;
      }
      if (this.estateAccounts?.length > 0) {
        const estateOwnershipShare = this.computeEstateShareForRegisteredInvestment;
        return this.computeValueForEstateFromEstateShare(this.atDeathValue, estateOwnershipShare);
      }

      return 0;
    }

    /**
     * @return {AssetCoOwner[] | LiabilityCoOwner[]}
     */
    get coOwners() {
      const coOwners = [];
      if (this.contacts) {
        this.contacts.forEach((contact) => {
          const coOwner =
            this.getModelName() === ModelType.ASSET
              ? AssetCoOwner.mapModel(contact)
              : LiabilityCoOwner.mapModel(contact);
          coOwner.type = ModelType.CONTACT;
          coOwners.push(coOwner);
        });
      }

      if (this.estateAccounts) {
        this.estateAccounts.forEach((estateAccount) => {
          const coOwner =
            this.getModelName() === ModelType.ASSET
              ? AssetCoOwner.mapModel(estateAccount)
              : LiabilityCoOwner.mapModel(estateAccount);
          coOwner.type = ModelType.ESTATE_ACCOUNT;
          coOwners.push(coOwner);
        });
      }
      return coOwners;
    }

    /**
     *
     * @return {number|float}
     */
    get computeEstateShareForRegisteredInvestment() {
      const beneficiaryShare = this.estateAccounts.reduce(
        (accum, beneficiary) => accum + Number.parseFloat(beneficiary.pivot?.shareOfOwnership),
        0
      );
      const estateShare = 100 - beneficiaryShare;
      if (estateShare <= 100 || estateShare >= 0) {
        return Math.round(estateShare * 100) / 100;
      }
      return 100;
    }

    computeValueForEstateFromEstateShare(value, estateOwnershipShare) {
      const estateValue = value * (Number.parseFloat(estateOwnershipShare) / 100);
      return Math.round(estateValue * 100) / 100;
    }

    removeMetaFromEstateAccounts() {
      this.estateAccounts?.forEach((estateAccount) => {
        delete estateAccount.meta;
      });
    }

    /**
     *
     * @return {number}
     */
    get ownershipValue() {
      return (this.computeEstateShareForOwnershipAssetOrLiability / 100) * this.estateWorth;
    }

    get ownershipLiabilityValue() {
      return (
        (this.computeEstateShareForOwnershipAssetOrLiability / 100) * this.currentBalanceAmount
      );
    }
  };
